<template>
  <div id="boas-praticas-indicador">
    <div
      v-if="loadingPage || loadingData"
      class="d-flex justify-content-center align-items-center"
      style="height: 50vh"
    >
      <b-spinner large variant="primary"></b-spinner>
    </div>

    <div v-else class="wrap">
      <div class="row">
        <div class="col-lg-4">
          <vue-select
            name="periodo"
            v-model="periodo"
            class="select-periodo"
            :clearable="false"
            :options="optionsPeriodo"
            label="label"
            placeholder="Período"
            @input="onChangePeriodo"
          >
            <template slot="option" slot-scope="option">
              {{ option.label }}
            </template>
            <div slot="no-options">Não há opções correspondentes</div>
          </vue-select>
        </div>
        <div class="col-lg-4">
          <vue-select
            id="select-tipo-equipe"
            name="tipo-equipe"
            v-model="tipoEquipe"
            class="select-tipo-equipe"
            :clearable="false"
            :options="optionsTipoEquipe"
            label="no_esf_eap"
            placeholder="Tipo Equipe"
            :disabled="disabledSelectTipoEquipe"
            @input="onChangeTipoEquipe"
          >
            <template slot="option" slot-scope="option">
              {{ option.no_esf_eap }}
            </template>
            <div slot="no-options">Não há opções correspondentes</div>
          </vue-select>
        </div>
      </div>

      <div class="heatmap">
        <div
          class="grid"
          :style="{
            gridTemplateColumns: `170px repeat(${categorias.length}, 1fr)`,
          }"
        >
          <div></div>

          <div
            v-for="(categoria, index) in categorias"
            :key="`col-${index}`"
            class="col-h"
            v-html="formatCategoria(categoria)"
          ></div>

          <template v-for="(linha, rowIndex) in linhas">
            <div :key="`label-${rowIndex}`" class="row-l">
              {{ linha.label }}
            </div>

            <div
              v-for="(valor, colIndex) in linha.vals"
              :key="`cell-${rowIndex}-${colIndex}`"
              class="c"
              :class="getCellClass(rowIndex, colIndex, valor)"
              :style="getCellStyle(rowIndex, colIndex, valor)"
              @mouseenter="showTooltip($event, rowIndex, colIndex, valor)"
              @mousemove="moveTooltip($event)"
              @mouseleave="hideTooltip"
            >
              <template v-if="valor !== null">
                {{ valor }}<span class="u">%</span>
              </template>

              <template v-else-if="deveMostrarTraco(rowIndex, colIndex)">
                <span class="empty-text">-</span>
              </template>
            </div>
          </template>
        </div>
        <div
          class="tooltip-custom"
          v-if="tooltip.show"
          :style="{ top: tooltip.y + 'px', left: tooltip.x + 'px' }"
        >
          <div class="tooltip-title">
            {{ tooltip.label }}
          </div>

          <div class="tooltip-row">
            <span>Numerador</span>
            <strong>{{ tooltip.numerador }}</strong>
          </div>

          <div class="tooltip-row">
            <span>Denominador</span>
            <strong>{{ tooltip.denominador }}</strong>
          </div>

          <div class="tooltip-row">
            <span>Resultado</span>
            <strong :style="{ color: tooltip.color }">
              {{ tooltip.valor }}<span v-if="tooltip.valor !== '-'">%</span>
            </strong>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import utils from '@/mixins/utils'

export default {
  name: 'BoasPraticasPorIndicador',
  props: {
    loadingPage: {
      type: Boolean,
      default: false,
    },
    dadosBoasPraticas: {
      type: Array,
      default: () => [],
    },
  },
  mixins: [utils],
  data() {
    return {
      dadosTabelaOriginais: [],
      dadosTabela: [],
      periodo: null,
      optionsPeriodo: [],
      loadingData: false,
      tipoEquipe: null,
      disabledSelectTipoEquipe: true,
      categorias: [],
      linhas: [],
      classificacoes: [],
      tooltip: {
        show: false,
        x: 0,
        y: 0,
        label: '',
        numerador: 0,
        denominador: 0,
        valor: 0,
        color: '',
      },
    }
  },
  computed: {
    optionsTipoEquipe() {
      return [
        {
          no_esf_eap: 'Municipal',
          ine_esf_eap: 'municipal',
        },
        ...(this.$store.state.equipesV2 || []),
      ]
    },
  },
  watch: {
    dadosBoasPraticas: {
      handler() {
        const periodos = Array.from(
          new Map(
            this.dadosBoasPraticas
              .filter((item) => item.mes_ano_quad)
              .map((item) => [
                item.mes_ano_quad,
                {
                  mes_ano_quad: item.mes_ano_quad,
                },
              ])
          ).values()
        )

        periodos.sort((a, b) => {
          const extrair = (texto) => {
            const mesAno = texto.split(' ')[0]
            const [mes, ano] = mesAno.split('/').map(Number)

            return { mes, ano }
          }

          const A = extrair(a.mes_ano_quad)
          const B = extrair(b.mes_ano_quad)

          if (A.ano !== B.ano) return B.ano - A.ano
          return B.mes - A.mes
        })

        this.optionsPeriodo = periodos.map((p) => ({
          value: p.mes_ano_quad,
          label: p.mes_ano_quad,
        }))

        this.periodo = this.optionsPeriodo[0] || null
        this.tipoEquipe = this.optionsTipoEquipe[0] || null

        if (this.periodo) {
          this.onChangePeriodo(this.periodo)
        }
      },
      deep: true,
      immediate: true,
    },
  },
  methods: {
    /**
     * Manipula ao alterar o período
     * @param {object} item
     */
    async onChangePeriodo(item) {
      if (!item || !item.value) {
        this.dadosTabelaOriginais = []
        this.dadosTabela = []
        this.categorias = []
        this.linhas = []
        return
      }

      try {
        this.loadingData = true
        this.disabledSelectTipoEquipe = true

        const dadosFiltrados = this.dadosBoasPraticas.filter(
          (registro) => registro.mes_ano_quad === item.value
        )

        this.dadosTabelaOriginais = dadosFiltrados
        this.dadosTabela = [...dadosFiltrados]

        if (this.tipoEquipe) {
          this.onChangeTipoEquipe(this.tipoEquipe)
        } else {
          this.montarTabela()
        }
      } catch (error) {
        console.log(error)
      } finally {
        this.loadingData = false
        this.disabledSelectTipoEquipe = false
      }
    },

    /**
     * Manipula ao alterar o tipo de equipe
     * @param {object} tipoEquipe
     */
    onChangeTipoEquipe(tipoEquipe) {
      if (!tipoEquipe) {
        this.dadosTabela = this.dadosTabelaOriginais
        this.montarTabela()
        return
      }

      const ineSelecionado = String(tipoEquipe.ine_esf_eap || '').toLowerCase()

      if (ineSelecionado === 'municipal') {
        this.dadosTabela = this.dadosTabelaOriginais.filter((item) => {
          return Number(item.nivel) === 1
        })
      } else {
        this.dadosTabela = this.dadosTabelaOriginais.filter((item) => {
          return (
            Number(item.nivel) === 2 &&
            String(item.ine || '') === String(tipoEquipe.ine_esf_eap || '')
          )
        })
      }

      this.montarTabela()
    },

    /**
     * Formata o texto da categoria substituindo quebras de linha por <br>
     * @param {string} texto
     * @returns {string}
     */
    formatCategoria(texto) {
      return texto.replace(/\n/g, '<br>')
    },

    /**
     * Retorna cor com base na classificação do indicador
     * @param {string} classificacao
     * @returns {object}
     */
    getCorPorClassificacao(classificacao) {
      const mapa = {
        Ótimo: {
          color: '#1212B6',
          background: 'rgba(18, 18, 182, 0.10)',
        },
        Otimo: {
          color: '#1212B6',
          background: 'rgba(18, 18, 182, 0.10)',
        },
        Bom: {
          color: '#38BA38',
          background: 'rgba(56, 186, 56, 0.10)',
        },
        Suficiente: {
          color: '#D5BA0A',
          background: 'rgba(213, 186, 10, 0.10)',
        },
        Regular: {
          color: '#D9453A',
          background: 'rgba(217, 69, 58, .09)',
        },
      }

      return (
        mapa[classificacao] || {
          color: '#999',
          background: 'rgba(0,0,0,0.05)',
        }
      )
    },

    /**
     * Retorna cor com base no valor percentual
     * @param {number} valor
     */
    getCorPorValor(valor) {
      const v = Number(valor)

      if (v >= 0 && v <= 25) {
        return {
          color: '#C46500',
          background: 'rgba(196, 101, 0, 0.12)',
        }
      }

      if (v > 25 && v <= 50) {
        return {
          color: '#D5BA0A',
          background: 'rgba(213, 186, 10, 0.12)',
        }
      }

      if (v > 50 && v <= 75) {
        return {
          color: '#38BA38',
          background: 'rgba(56, 186, 56, 0.12)',
        }
      }

      if (v > 75) {
        return {
          color: '#1212B6',
          background: 'rgba(18, 18, 182, 0.12)',
        }
      }
    },

    /**
     * Retorna o estilo da célula (background e cor do texto)
     * @param {number|null} valor
     * @returns {object}
     */
    getCellStyle(valor) {
      if (valor === null) return {}

      const cor = this.getCorPorValor(valor)

      return {
        background: cor.background,
        color: cor.color,
      }
    },

    /**
     * Formata o nome da categoria
     * @param {string} texto
     * @returns {string}
     */
    formatarNomeCategoria(texto) {
      if (!texto) return ''

      if (texto.includes('Prevenção do câncer na mulher')) {
        return 'Saúde da\nMulher'
      }

      if (texto.includes('Gestante e puérpera')) {
        return 'Gestantes e\npuérperas'
      }

      return texto
        .replace(/^C\d+\s*-\s*/i, '')
        .replace('Pessoa com ', 'Pessoa\n')
        .replace('Pessoa ', 'Pessoa\n')
        .replace('Desenvolvimento infantil', 'Desenvolvimento \ninfantil')
    },

    /**
     * Nomes das linhas da tabela
     */
    linhasBase() {
      return [
        'Resultado indicador',
        'Consultas',
        'Visitas',
        'Peso e altura',
        'Vacinas',
        'Aferição PA',
        'Primeira consulta',
        'Exames 1° trimestre',
        'Exames 3° trimestre',
        'Avaliação odontológica',
        'Hemoglobina',
        'Avaliação pés',
        'Colo útero',
        'Mama',
      ]
    },

    /**
     * Retorna a configuração de chaves (numerador, denominador e resultado)
     * para uma linha específica com base no indicador
     * @param {string} label Nome da linha (ex: 'Consultas', 'Vacinas', etc.)
     * @param {number} indicadorId Código do indicador (ex: 51, 52, 48...)
     * @returns {object|null} Configuração das chaves ou null caso não exista
     */
    getConfigLinhaPorIndicador(label, indicadorId) {
      const mapa = {
        'Resultado indicador': {
          default: {
            numeradorKey: 'numerador',
            denominadorKey: 'denominador',
            resultadoKey: 'resultado',
          },
        },

        Consultas: {
          51: { numeradorKey: 'num_fai', resultadoKey: 'res_fai' },
          52: { numeradorKey: 'num_fai', resultadoKey: 'res_fai' },
          48: { numeradorKey: 'num_fai', resultadoKey: 'res_fai' },
          49: { numeradorKey: 'num_fai', resultadoKey: 'res_fai' },
          50: { numeradorKey: 'num_fai', resultadoKey: 'res_fai' },
          47: { numeradorKey: 'num_fai', resultadoKey: 'res_fai' },
        },

        Visitas: {
          51: { numeradorKey: 'num_fvdt', resultadoKey: 'res_fvdt' },
          52: { numeradorKey: 'num_fvdt', resultadoKey: 'res_fvdt' },
          48: { numeradorKey: 'num_fvdt', resultadoKey: 'res_fvdt' },
          49: { numeradorKey: 'num_fvdt', resultadoKey: 'res_fvdt' },
          50: { numeradorKey: 'num_fvdt', resultadoKey: 'res_fvdt' },
        },

        'Peso e altura': {
          51: { numeradorKey: 'num_pes_alt', resultadoKey: 'res_pes_alt' },
          52: { numeradorKey: 'num_pes_alt', resultadoKey: 'res_pes_alt' },
          48: { numeradorKey: 'num_pes_alt', resultadoKey: 'res_pes_alt' },
          49: { numeradorKey: 'num_pes_alt', resultadoKey: 'res_pes_alt' },
          50: { numeradorKey: 'num_pes_alt', resultadoKey: 'res_pes_alt' },
        },

        Vacinas: {
          51: { numeradorKey: 'num_vac', resultadoKey: 'res_vac' },
          52: { numeradorKey: 'num_vac', resultadoKey: 'res_vac' },
          49: { numeradorKey: 'num_vac', resultadoKey: 'res_vac' },
          47: {
            numeradorKey: 'num_vac',
            denominadorKey: 'den_vac',
            resultadoKey: 'res_vac',
          },
        },

        'Aferição PA': {
          52: { numeradorKey: 'num_pa', resultadoKey: 'res_pa' },
          48: { numeradorKey: 'num_pa', resultadoKey: 'res_pa' },
          50: { numeradorKey: 'num_pa', resultadoKey: 'res_pa' },
        },

        'Primeira consulta': {
          51: {
            numeradorKey: 'num_fai_1consulta',
            resultadoKey: 'res_fai_1consulta',
          },
          52: {
            numeradorKey: 'num_fai_1consulta',
            resultadoKey: 'res_fai_1consulta',
          },
        },

        'Exames 1° trimestre': {
          52: {
            numeradorKey: 'num_enx_sol_aval_1trim',
            resultadoKey: 'res_enx_sol_aval_1trim',
          },
        },

        'Exames 3° trimestre': {
          52: {
            numeradorKey: 'num_enx_sol_aval_3trim',
            resultadoKey: 'res_enx_sol_aval_3trim',
          },
        },

        'Avaliação odontológica': {
          52: { numeradorKey: 'num_fao', resultadoKey: 'res_fao' },
        },

        Hemoglobina: {
          48: {
            numeradorKey: 'num_hemoglobina',
            resultadoKey: 'res_hemoglobina',
          },
        },

        'Avaliação pés': {
          48: { numeradorKey: 'num_pes', resultadoKey: 'res_pes' },
        },

        'Colo útero': {
          47: {
            numeradorKey: 'num_colo_utero',
            denominadorKey: 'den_colo_utero',
            resultadoKey: 'res_colo_utero',
          },
        },

        Mama: {
          47: {
            numeradorKey: 'num_mama',
            denominadorKey: 'den_mama',
            resultadoKey: 'res_mama',
          },
        },
      }

      const linha = mapa[label]
      if (!linha) return null

      return linha[indicadorId] || linha.default || null
    },

    /**
     * Monta categorias e linhas da tabela com base nos indicadores
     */
    montarTabela() {
      if (!this.dadosTabela.length) {
        this.categorias = []
        this.linhas = []
        this.classificacoes = []
        return
      }

      const indicadores = this.dadosTabela
        .map((item) => {
          const dadosParse = this.parseObjectJson(item.dados)

          return {
            ...dadosParse,
            ds_indicador: item.ds_indicador,
            indicador: item.indicador,
            nivel: item.nivel,
            ine: item.ine,
          }
        })
        .filter(Boolean)
        .sort((a, b) => {
          const getOrdem = (texto) => {
            const match = String(texto || '').match(/^C(\d+)/i)
            return match ? Number(match[1]) : 999
          }

          return getOrdem(a.ds_indicador) - getOrdem(b.ds_indicador)
        })

      if (!indicadores.length) {
        this.categorias = []
        this.linhas = []
        this.classificacoes = []
        return
      }

      this.categorias = indicadores.map((item) =>
        this.formatarNomeCategoria(item.ds_indicador)
      )

      this.classificacoes = indicadores.map(
        (item) => item.classificacao || null
      )

      const linhasFixas = this.linhasBase()

      this.linhas = linhasFixas.map((label) => {
        const vals = indicadores.map((indicador) => {
          const indicadorId = Number(indicador.indicador)
          const config = this.getConfigLinhaPorIndicador(label, indicadorId)

          if (!config) return null

          const valor = indicador[config.resultadoKey]
          return valor !== undefined && valor !== null ? Number(valor) : null
        })

        const meta = indicadores.map((indicador) => {
          const indicadorId = Number(indicador.indicador)
          const config = this.getConfigLinhaPorIndicador(label, indicadorId)

          if (!config) return null

          const valor = indicador[config.resultadoKey]

          return {
            numerador:
              config.numeradorKey &&
              indicador[config.numeradorKey] !== undefined
                ? indicador[config.numeradorKey]
                : null,
            denominador:
              config.denominadorKey &&
              indicador[config.denominadorKey] !== undefined
                ? indicador[config.denominadorKey]
                : indicador.denominador ?? null,
            valor: valor !== undefined && valor !== null ? Number(valor) : null,
          }
        })

        return {
          label,
          vals,
          meta,
        }
      })
    },

    /**
     * Verifica se a célula nula deve exibir traço com fundo cinza
     * @param {number} rowIndex
     * @param {number} colIndex
     * @returns {boolean}
     */
    deveMostrarTraco(rowIndex, colIndex) {
      const meta = this.linhas[rowIndex]?.meta?.[colIndex]
      return meta?.denominador !== null && meta?.denominador !== undefined
    },

    /**
     * Retorna as classes da célula
     * @param {number} rowIndex
     * @param {number} colIndex
     * @param {number|null} valor
     * @returns {object}
     */
    getCellClass(rowIndex, colIndex, valor) {
      return {
        has: valor !== null,
        empty: valor === null && this.deveMostrarTraco(rowIndex, colIndex),
      }
    },

    /**
     * Retorna o estilo da célula
     * @param {number} rowIndex
     * @param {number} colIndex
     * @param {number|null} valor
     * @returns {object}
     */
    getCellStyle(rowIndex, colIndex, valor) {
      if (valor !== null) {
        const cor = this.getCorPorValor(valor)

        return {
          background: cor.background,
          color: cor.color,
        }
      }

      if (this.deveMostrarTraco(rowIndex, colIndex)) {
        return {
          background: '#f1f3f5',
          color: '#adb5bd',
        }
      }

      return {}
    },

    /**
     * Exibe o tooltip ao passar o mouse sobre a célula
     * @param {MouseEvent} event
     * @param {number} rowIndex
     * @param {number} colIndex
     * @param {number|null} valor
     */
    showTooltip(event, rowIndex, colIndex, valor) {
      const meta = this.linhas[rowIndex]?.meta?.[colIndex]

      const valorTooltip = meta?.valor ?? valor
      const cor =
        valorTooltip !== null && valorTooltip !== undefined
          ? this.getCorPorValor(valorTooltip).color
          : '#adb5bd'

      this.tooltip = {
        show: true,
        x: event.clientX + 16,
        y: event.clientY - 10,
        label: `${this.linhas[rowIndex].label} · ${this.categorias[
          colIndex
        ].replace('\n', ' ')}`,
        numerador:
          meta?.numerador !== null && meta?.numerador !== undefined
            ? meta.numerador
            : '-',
        denominador:
          meta?.denominador !== null && meta?.denominador !== undefined
            ? meta.denominador
            : '-',
        valor:
          valorTooltip !== null && valorTooltip !== undefined
            ? valorTooltip
            : '-',
        color: cor,
      }
    },

    /**
     * Atualiza a posição do tooltip conforme o movimento do mouse
     * @param {MouseEvent} event
     */
    moveTooltip(event) {
      this.tooltip.x = event.clientX + 16
      this.tooltip.y = event.clientY - 10
    },

    /**
     * Oculta o tooltip
     */
    hideTooltip() {
      this.tooltip.show = false
    },
  },
}
</script>

<style scoped lang="scss">
$text: #1a1a2e;
$muted: #9090a8;

#boas-praticas-indicador {
  padding: 24px 0 32px;

  .wrap {
    width: 100%;
    margin: 0 auto;
  }

  .title {
    font-size: 20px;
    font-weight: 700;
    letter-spacing: -0.4px;
    color: $text;
    margin-bottom: 28px;
  }

  .heatmap {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    margin-top: 30px;
  }

  .grid {
    display: grid;
    gap: 3px;
    min-width: 780px;
  }

  .col-h {
    font-size: 10.5px;
    font-weight: 600;
    color: $muted;
    text-align: center;
    padding: 0 2px 14px;
    line-height: 1.35;
  }

  .row-l {
    font-size: 13px;
    font-weight: 500;
    color: $text;
    display: flex;
    align-items: center;
    padding-right: 14px;
    white-space: nowrap;
  }

  .c {
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 46px;
    font-size: 14px;
    font-weight: 700;
    user-select: none;

    &.has {
      cursor: default;
      transition: transform 0.12s, box-shadow 0.12s;

      &:hover {
        transform: scale(1.04);
        box-shadow: 0 2px 14px rgba(0, 0, 0, 0.08);
        z-index: 2;
      }
    }

    .u {
      font-size: 11px;
      font-weight: 400;
      opacity: 0.65;
      margin-left: 1px;
    }
  }

  .empty {
    background: #f1f3f5;
    color: #adb5bd;
  }

  .empty-text {
    font-size: 18px;
    font-weight: 600;
  }

  @media (max-width: 700px) {
    padding: 18px 0 24px;

    .title {
      font-size: 17px;
      margin-bottom: 20px;
    }

    .row-l {
      font-size: 11px;
      padding-right: 8px;
    }

    .c {
      height: 40px;
      font-size: 12px;
    }
  }

  .tooltip-custom {
    position: fixed;
    z-index: 999;
    background: #fff;
    border: 1px solid #e8e8ee;
    border-radius: 12px;
    padding: 14px 18px;
    box-shadow: 0 8px 30px rgba(0, 0, 0, 0.1);
    min-width: 200px;
    pointer-events: none;

    .tooltip-title {
      font-size: 12px;
      font-weight: 600;
      color: #9090a8;
      margin-bottom: 10px;
    }

    .tooltip-row {
      display: flex;
      justify-content: space-between;
      padding: 5px 0;

      & + .tooltip-row {
        border-top: 1px solid #e8e8ee;
      }

      span {
        color: #9090a8;
        font-size: 13px;
      }

      strong {
        font-size: 14px;
        font-weight: 700;
      }
    }
  }
}
</style>
